import { StorageManager } from 'src/app/common/storage-manager.class';
import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    RepeatableComputeContext,
    RepeatableLegacyAggregateTargetInput,
    RepeatableSubField,
    RepeatableSubFieldContext,
    FilteredValue,
    ReactiveFormChangedField,
    ReactiveFormValue,
    DropdownData,
    DropdownFilterContext,
    ReactiveDropdownDataSource,
    ReactiveDropdownKeySelector,
    ReactiveDropdownCatalogs,
    Value,
} from '@fitogest/reactive-form-builder/interfaces';

import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { ProductModel } from 'src/app/models/productos/product.model';
import { StockageModel } from 'src/app/models/stock/stokage.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';

import { EntradasEnvironmentDefinition } from '../../environment-variables';
import { EntradasListsDefinition } from './list-variables';


export interface EntradasDropdownEndpoints {
    readonly fincas: unknown;
    readonly sectores: unknown;
    readonly parcelas: unknown;
    readonly comercios: unknown;
}

type EntradasDropdownEndpointKey = keyof EntradasDropdownEndpoints;

type ReactiveFormContextReader = {
    readonly model: {
        get: (fieldName: string) => { readonly value: unknown } | null;
    };
    readonly getValue?: (fieldName: string) => unknown;
};

export class EntradasFormDefinition {

    public formType!: FormType;

    public env: EntradasEnvironmentDefinition = new EntradasEnvironmentDefinition();

    public lists: EntradasListsDefinition = new EntradasListsDefinition();

    public formFields!: Form<StockageModel>;

    public fieldNames: FormField<any>[] = [];

    public fieldLabels: string[] = [];

    public cliente = StorageManager.getClient();

    public usuario = StorageManager.getUser();

    public model: ReactiveFormValue<StockageModel> = {} as ReactiveFormValue<StockageModel>;

    public tag: ReactiveFormChangedField<StockageModel> = '';

    private readonly dropdownEndpoints: EntradasDropdownEndpoints | null;

    constructor(
        type: FormType,
        model: StockageModel,
        table: (string[] | string[][])[] | null,
        dropdownEndpoints: EntradasDropdownEndpoints | null = null
    ) {
        this.formType = +type;
        this.dropdownEndpoints = dropdownEndpoints;
        this.model = { ...this.model, ...model };

        if (this.env.forcesPlantType && !this.model.tipo_dato) {
            this.model.tipo_dato = 'Semilla';
        }

        this.formFields = {
            name: 'stock-entradas',
            header: 'Registrar movimiento',
            type: this.formType,
            sections: this.getFormSections(),
            table: {
                title: '',
                showIndex: false,
                data: table?.[0],
                style: table?.[1]
            },
            uploadButton: {
                isEnabled: false,
                label: ''
            },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
            previousDataTransform: (previousData: StockageModel): StockageModel => this.preparePreviousData(previousData)
        };
    }

    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: ['id_finca', 'id_sector'],
            deferred: ['id_comercio', 'ubicacion', 'ubicacion_destino'],
            lazy: []
        };
    }

    private preparePreviousData(previousData: StockageModel): StockageModel {
        this.model = { ...this.model, ...previousData };
        this.refreshProductosList();
        this.refreshLineFieldLabels();

        return { ...previousData };
    }

    public getFormSections(): FormSection<any>[] {
        return [
            {
                name: 'Datos generales',
                fields: this.getGeneralFields(),
                width: 12,
                isVisible: true,
                order: 1
            },
            {
                name: 'Líneas',
                fields: this.getLineFields(),
                width: 12,
                isVisible: true,
                order: 2
            },
            {
                name: '',
                fields: this.getObservationFields(),
                width: 12,
                isVisible: true,
                order: 3
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 4
            }
        ];
    }

    public refreshProductosList(): void {
        const target = this.lists.productos;
        const tipoDato = String(this.model.tipo_dato ?? '');

        if (this.env.appName === 'detorres') {
            const base = this.lists.productosAll.values ?? [];

            target.values = base.map((item) => {
                const value = item.value as ProductModel | null;

                if (!value) {
                    return item;
                }

                return {
                    ...item,
                    label: `${value.nombre} (${value.tipo})`
                };
            });

            target.filtered = [...target.values];
            return;
        }

        if (tipoDato === 'Semilla' || tipoDato === 'Planta') {
            target.values = [...this.lists.cultivosAll.values];
            target.filtered = [...this.lists.cultivosAll.filtered];
            return;
        }

        const productTypeMap: Record<string, string> = {
            Fitosanitario: 'fitosanitario',
            Fertilizante: 'fertilizante',
            'Ganadería': 'ganaderia',
            Otros: 'otros',
            Bio: 'bio',
        };

        const requiredType = productTypeMap[tipoDato];

        if (!requiredType) {
            target.values = [];
            target.filtered = [];
            return;
        }

        const values = this.lists.productosAll.values.filter((item) => {
            const value = item.value as ProductModel | null;

            return value?.tipo === requiredType;
        });

        const filtered = this.lists.productosAll.filtered.filter((item) => {
            const value = item.value as ProductModel | null;

            return value?.tipo === requiredType;
        });

        target.values = [...values];
        target.filtered = [...filtered];
    }

    private getGeneralFields(): FormField<any>[] {
        const generalFields: FormField<any>[] = [
            {
                name: 'id_finca',
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                ...this.createDropdownDataSourceField('fincas', 'nombre', 'id'),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'fecha',
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: this.env.appName === 'viverosmuzale' ? 4 : 2
            },
            {
                name: 'tipo',
                label: 'Tipo',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.tipoEntrada,
                    defaultValue: 'E'
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: this.env.appName === 'viverosmuzale' ? 5 : 3
            },
            {
                name: 'motivo',
                label: 'Motivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.env.appName === 'covidai'
                        ? this.lists.motivosCovidai
                        : this.lists.motivos
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockMotivo
                },
                order: 4
            },
            {
                name: 'tipo_dato',
                label: this.env.appName === 'detorres' ? 'Tipos detectados' : 'Tipo de producto',
                input: {
                    type: this.env.appName === 'detorres' ? InputType.HIDDEN : InputType.DROPDOWN,
                    isEditable: this.env.appName !== 'detorres',
                    data: this.lists.tiposDeDato,
                    defaultValue: this.defaultDataType

                },
                computed: {
                    dependsOn: ['ids_productos'],
                    resolve: (): string | undefined => this.resolveTipoDatoValue()
                },
                updateValue: (): string | undefined => this.resolveTipoDatoValue(),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: this.env.showStockTipoDeDato,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockTipoDeDato &&
                        this.env.appName !== 'viverosblanco' &&
                        this.env.appName !== 'detorres'
                },
                order: this.env.appName === 'viverosmuzale' ? 6 : 5
            },
            {
                name: 'id_comercio',
                dependencies: [
                    {
                        source: 'tipo',
                        target: 'id_comercio',
                        kind: 'filter'
                    }
                ],
                label: this.getCompanyFieldLabel(),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.comercios,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<Compradores>, context?: DropdownFilterContext<Compradores>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            if (this.env.appName === 'covidai') {
                                return true;
                            }

                            const tipo = String(context?.model.get('tipo')?.value ?? this.model.tipo ?? 'E');
                            const companyType = String(it.value.tipo ?? '');

                            if (tipo === 'S') {
                                return companyType === 'Comercio' || companyType === 'Cliente';
                            }

                            return companyType === 'Proveedor';
                        },
                        swap: (field: FormField<any>): void => {
                            field.label = this.getCompanyFieldLabel();
                        }
                    }
                },
                ...this.createDropdownDataSourceField('comercios', 'razon_social', 'id'),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockProveedor && this.env.showStockProveedorClienteDropdown
                },
                order: 6
            },
            {
                name: 'proveedor',
                label: 'Proveedor',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                computed: {
                    dependsOn: ['id_comercio'],
                    resolve: (context): string | undefined => {
                        if (!this.env.showStockProveedorClienteDropdown) {
                            return undefined;
                        }

                        const selected = this.resolveComercioFromValue(
                            context.getValue('id_comercio') ?? this.model['id_comercio']
                        );

                        return selected?.razon_social ?? '';
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockProveedor && !this.env.showStockProveedorClienteDropdown
                },
                order: 7
            },
            {
                name: 'cif_proveedor',
                label: 'NIF/CIF',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockCifProveedor
                },
                order: 8
            },
            {
                name: 'albaran',
                label: 'Albarán/Factura',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 9
            },
            {
                name: 'id_sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: 'id_sector',
                        kind: 'filter'
                    }
                ],
                label: this.env.sectorName,
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.sectores,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<SectorModel>, context?: DropdownFilterContext<SectorModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const fincaId = this.resolveSelectedDropdownId('id_finca', this.lists.fincas, context);

                            return this.sameValue(it.value.id_finca, fincaId);
                        }
                    }
                },
                ...this.createDropdownDataSourceField('sectores', 'nombre', 'id'),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'covidai' || this.env.appName === 'sendra'
                        || this.env.appName === 'viverosmuzale'
                },
                order: this.env.appName === 'viverosmuzale' ? 2 : 10
            },
            {
                name: 'ubicacion',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: 'ubicacion',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: 'ubicacion',
                        kind: 'filter'
                    }
                ],
                label: this.env.appName === 'viverosmuzale'
                    ? this.env.parcelaName
                    : this.env.parcelaName + ' de origen',
                input: this.buildUbicacionInput(),
                ...this.createUbicacionDataSourceField(),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockUbicacion ||
                        this.env.appName === 'covidai' ||
                        this.env.appName === 'bravosol' ||
                        this.env.appName === 'viverosmuzale'
                },
                order: this.env.appName === 'viverosmuzale' ? 3 : 11
            },
            {
                name: 'ubicacion_destino',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: 'ubicacion_destino',
                        kind: 'filter'
                    }
                ],
                label: this.env.parcelaName + ' de destino',
                input: this.buildUbicacionDestinoInput(),
                ...this.createUbicacionDestinoDataSourceField(),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible:
                        this.env.showStockUbicacionDestino ||
                        this.env.appName === 'covidai' ||
                        this.env.appName === 'bravosol' ||
                        this.env.appName === 'viverosblanco'
                },
                order: 12
            },
            {
                name: 'fecha_injerto',
                label: 'Fecha injerto',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'viverosblanco'
                },
                order: 13
            },
            {
                name: 'articulo',
                label: 'Artículo',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.articulos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'cod_producto'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'bravosol'
                },
                order: 14
            },
            {
                name: 'unidades_medida',
                label: 'Unidades de medida',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.unidades
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockTipoDeDato &&
                        !this.env.showStockAdditionalFields &&
                        this.env.appName !== 'pyf' &&
                        this.env.applicationType !== 'viverogest'
                },
                order: 15
            }
        ];
        this.fieldNames = this.fieldNames.concat(generalFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(generalFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return generalFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLineFields(): FormField<any>[] {
        const lineFields: FormField<any>[] = [
            {
                name: 'lineas_group',
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: true,
                    repeatableGroupConfiguration: {
                        rows: this.env.maxRows,
                        addButtonLabel: 'Añadir línea',
                        parentDependencies: ['tipo_dato'],
                        bindings: this.getRepeatableBindings(),
                        serialization: {
                            separator: ';'
                        },
                        legacyAggregateSerialization: {
                            trimTrailingEmptyRows: true,
                            trimTrailingEmptyRowsBy: 'producto',
                            targets: this.getRepeatableAggregateTargets()
                        },
                        subFields: this.getRepeatableSubFields()
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
        this.fieldNames = this.fieldNames.concat(lineFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(lineFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return lineFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getObservationFields(): FormField<any>[] {
        const observationFields: FormField<any>[] = [
            {
                name: 'observaciones',
                label: this.env.appName === 'armentia'
                    ? 'Observaciones (tratamiento)'
                    : 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        rows: 4,
                        cols: 30,
                        hasVoiceTranscription: false,
                        canTakePhoto: false,
                        photoField: undefined
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
        this.fieldNames = this.fieldNames.concat(observationFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(observationFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return observationFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<any>[] {
        const fields: FormField<any>[] = [
            this.hiddenField('id'),
            this.hiddenField('id_cliente', ['id_finca'], (context): string => {
                const finca = this.resolveSelectedDropdownEntity('id_finca', this.lists.fincas, context);

                return String(
                    finca?.id_cliente ??
                    this.model.id_cliente ??
                    this.cliente?.id ??
                    ''
                );
            }),
            this.hiddenField('id_usuario', ['id_finca'], (context): string => {
                const finca = this.resolveSelectedDropdownEntity('id_finca', this.lists.fincas, context);

                return String(
                    finca?.id_usuario ??
                    this.model.id_usuario ??
                    this.usuario?.id ??
                    ''
                );
            }),
            this.hiddenField('ids_tipos', ['ids_productos'], (): string => {
                if (!this.env.showStockTipoDeDato) {
                    return '';
                }

                return String(this.model.ids_productos ?? '');
            }),
        ];

        if (this.env.appName === 'covidai') {
            fields.push(
                this.hiddenField('lote1', ['array_lotes'], (): string => this.firstToken(this.model.array_lotes)),
                this.hiddenField('lote1_conselleria', ['array_lotes_conselleria'], (): string =>
                    this.firstToken(this.model.array_lotes_conselleria)
                )
            );
        }

        return fields;
    }

    private getRepeatableBindings(): Record<string, string> {
        const bindings: Record<string, string> = {
            producto: 'ids_productos',
            formato: 'formato',
            udsBandeja: 'uds_bandeja',
            cantidad: 'array_unidades',
            udsProducidas: 'uds_producidas',
            lote: 'array_lotes',
            marcaComercial: 'array_marcas',
            envase: 'array_envases',
            loteConselleria: 'array_lotes_conselleria',
            variedad: 'id_variedad',
            tipoSemilla: 'tipo_semilla',
            codMb: 'cod_mb',
            categoriaMfr: 'categoria_mfr',
        };

        if (this.env.showStockAdditionalFields) {
            bindings['unidadMedida'] = 'unidades_medida';
        }

        return bindings;
    }

    private getRepeatableAggregateTargets(): Record<string, RepeatableLegacyAggregateTargetInput | RepeatableLegacyAggregateTargetInput[]> {
        const bindings = this.getRepeatableBindings();
        const targets: Record<string, RepeatableLegacyAggregateTargetInput | RepeatableLegacyAggregateTargetInput[]> = {};

        for (const [logicalKey, fieldName] of Object.entries(bindings)) {
            targets[logicalKey] = { fieldName, emptyValue: '' };
        }

        targets['producto'] = this.env.showStockTipoDeDato
            ? [
                { fieldName: 'ids_productos', emptyValue: '' },
                { fieldName: 'ids_tipos', emptyValue: '' }
            ]
            : { fieldName: 'ids_productos', emptyValue: '' };

        return targets;
    }

    private getRepeatableSubFields(): RepeatableSubField[] {
        return [
            {
                key: 'producto',
                label: this.getProductFieldLabel(),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        controlValueKey: 'id',
                        backendValueKey: 'id',
                        rawIdentityKeys: ['id', 'codigo', 'referencia', 'nombre'],
                        virtualScroll: true
                    }
                },
                parentDependencies: ['tipo_dato'],
                getData: (): EntradasListsDefinition['productos'] => this.lists.productos
            },
            {
                key: 'variedad',
                label: 'Variedad',
                isVisible: (): boolean => (
                    (this.env.appName === 'pyf' || this.env.appName === 'sendra' || this.env.appName === 'viverosmuzale') &&
                    this.isPlantOrSeedData()
                ),
                dependsOn: 'producto',
                dependencyBehaviour: {
                    clearOnParentChange: true,
                    keepCurrentValueIfStillAvailable: true,
                    autoSelect: 'singleOption'
                },
                getData: (ctx: RepeatableSubFieldContext): DropdownData<string> => this.buildRepeatableVariedadData(ctx),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.auxCultivos
                }
            },
            {
                key: 'formato',
                label: 'Formato',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.formatosViverogest,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo'
                    }
                }
            },
            {
                key: 'udsBandeja',
                label: 'Uds. bandeja',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                dependsOn: 'formato',
                dependencyBehaviour: {
                    clearOnParentChange: false
                },
                computeValue: (ctx: RepeatableComputeContext): string => this.computeRepeatableUdsBandeja(ctx),
                input: {
                    type: InputType.NUMERIC,
                    isEditable: false
                }
            },
            {
                key: 'tipoSemilla',
                label: 'Categoría',
                isVisible: (): boolean => this.env.appName === 'pyf' && this.isPlantOrSeedData(),
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.listaSemilla
                }
            },
            {
                key: 'cantidad',
                label: this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta'
                    ? 'Uds de bandejas'
                    : 'Cantidad',
                input: {
                    type: InputType.NUMERIC,
                    isEditable: true
                }
            },
            {
                key: 'udsProducidas',
                label: 'Uds. producidas',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                dependsOn: ['formato', 'cantidad'],
                dependencyBehaviour: {
                    clearOnParentChange: false
                },
                computeValue: (ctx: RepeatableComputeContext): string => this.computeRepeatableUdsProducidas(ctx),
                input: {
                    type: InputType.NUMERIC,
                    isEditable: false
                }
            },
            {
                key: 'marcaComercial',
                label: 'Marca comercial',
                isVisible: (): boolean => this.env.showStockMarcaComercial,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'envase',
                label: 'Envase',
                isVisible: (): boolean => this.env.showStockEnvase,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'lote',
                label: 'Lote propio',
                isVisible: (): boolean => this.env.showStockLotePropio,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'codMb',
                label: 'Cod. MB/CPM',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'categoriaMfr',
                label: 'Categoría MFR',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.SWITCH,
                    isEditable: true
                }
            },
            {
                key: 'loteConselleria',
                label: 'Lote conselleria',
                isVisible: (): boolean => this.env.usesConselleriaLot,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'unidadMedida',
                label: 'Unidad medida',
                isVisible: (): boolean => this.env.showStockAdditionalFields && this.env.applicationType !== 'viverogest',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.unidades,
                }
            }
        ];
    }

    private getCompanyFieldLabel(): string {
        return String(this.model.tipo ?? 'E') === 'S'
            ? this.env.clienteName
            : 'Proveedor';
    }

    private getProductFieldLabel(): string {
        if (this.env.appName === 'detorres') {
            return 'Producto';
        }

        if (!this.env.showStockTipoDeDato) {
            if (this.env.appName === 'vginer') {
                return 'Fertilizante';
            }

            if (this.env.appName === 'covidai') {
                return 'Variedad';
            }

            return 'Producto/Marca comercial';
        }

        if (this.env.appName === 'momparler') {
            return 'Producto o planta';
        }

        if (this.env.appName === 'viverosblanco' || this.env.appName === 'covidai') {
            return 'Producto';
        }

        return String(this.model.tipo_dato || 'Producto');
    }

    private resolveComercioFromValue(value: unknown): Compradores | null {
        if (this.isRecord(value)) {
            return value as unknown as Compradores;
        }

        const token = String(value ?? '').trim();

        if (token.length === 0) {
            return null;
        }

        const options = [...this.lists.comercios.values, ...this.lists.comercios.filtered];

        for (const option of options) {
            const comercio = option.value as Compradores | null;

            if (
                String(comercio?.id ?? '') === token
                || String(comercio?.razon_social ?? '') === token
                || option.label === token
            ) {
                return comercio;
            }
        }

        return null;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private resolveTipoDatoValue(): string | undefined {
        if (this.env.forcesSeedType && !this.hasNonEmptyValue(this.model.tipo_dato)) {
            return 'Semilla';
        }

        if (this.env.forcesPlantType && !this.hasNonEmptyValue(this.model.tipo_dato)) {
            return 'Planta';
        }

        if (this.env.appName === 'detorres') {
            return this.computeDetorresTipoDato();
        }

        return undefined;
    }

    private computeDetorresTipoDato(): string {
        const ids = String(this.model.ids_productos ?? '')
            .split(';')
            .map((id) => id.trim())
            .filter((id) => id !== '');

        if (ids.length === 0) {
            return '';
        }

        const values = this.lists.productosAll.values as Array<{ value: ProductModel | null; label: string }>;

        const types = ids
            .map((id) => {
                const match = values.find((item) => String(item.value?.id ?? '') === id);
                const tipo = String(match?.value?.tipo ?? '');

                if (tipo === '') {
                    return '';
                }

                return tipo.charAt(0).toUpperCase() + tipo.slice(1).toLowerCase();
            })
            .filter((tipo) => tipo !== '');

        return types.join(';');
    }

    private isPlantOrSeedData(): boolean {
        return ['Planta', 'Semilla'].includes(String(this.model.tipo_dato ?? ''));
    }

    private buildRepeatableVariedadData(ctx: RepeatableSubFieldContext): DropdownData<string> {
        const producto = ctx.resolveEntity?.<Record<string, unknown>>('producto') ?? this.resolveRepeatableProducto(ctx.row['producto']);
        const cultivoSelect = String(producto?.['cultivo'] ?? '').trim();

        if (cultivoSelect === '') {
            return { values: [], filtered: [] };
        }

        const seen = new Set<string>();
        const opciones = this.lists.auxCultivos.filtered
            .filter((item) => String(item.label ?? '').trim() === cultivoSelect)
            .map((item): Value<string> => {
                const value = item.value as VisitasVariedadMesModel | null;

                return {
                    label: String(value?.variedad ?? ''),
                    value: String(value?.id ?? ''),
                    raw: value
                };
            })
            .filter((item) => {
                const key = `${item.label}|${String(item.value ?? '')}`;

                if (item.label === '' || String(item.value ?? '') === '' || seen.has(key)) {
                    return false;
                }

                seen.add(key);
                return true;
            });

        return {
            values: [...opciones],
            filtered: [...opciones]
        };
    }

    private computeRepeatableUdsBandeja(ctx: RepeatableComputeContext): string {
        const udsBandeja = this.resolveFormatoUdsBandeja(ctx.rows[ctx.rowIndex]?.['formato']);

        return udsBandeja === null ? '' : String(udsBandeja);
    }

    private computeRepeatableUdsProducidas(ctx: RepeatableComputeContext): string {
        const udsBandeja = this.resolveFormatoUdsBandeja(ctx.rows[ctx.rowIndex]?.['formato']);
        const cantidad = this.parseRepeatableDecimal(ctx.rows[ctx.rowIndex]?.['cantidad']);

        if (udsBandeja === null || cantidad === 0) {
            return '';
        }

        return String(cantidad * udsBandeja);
    }

    private resolveFormatoUdsBandeja(rawCodigo: unknown): number | null {
        const codigo = String(rawCodigo ?? '').trim();

        if (codigo === '') {
            return null;
        }

        return this.lists.formatosViverogest.filtered.find(
            (item) => item.value?.codigo === codigo
        )?.value?.uds_bandeja ?? null;
    }

    private parseRepeatableDecimal(rawValue: unknown): number {
        const parsed = Number(String(rawValue ?? '0').replace(',', '.'));

        return Number.isFinite(parsed) ? parsed : 0;
    }

    private resolveRepeatableProducto(rawProducto: unknown): Record<string, unknown> | null {
        const productoId = String(rawProducto ?? '').trim();

        if (productoId === '') {
            return null;
        }

        const map = new Map<string, Record<string, unknown>>();

        for (const item of this.lists.productos.values ?? []) {
            const value = item.value as Record<string, unknown> | null;

            if (value?.['id'] != null) {
                map.set(String(value['id']), value);
            }
        }

        for (const item of this.lists.productos.filtered ?? []) {
            const value = item.value as Record<string, unknown> | null;

            if (value?.['id'] != null) {
                map.set(String(value['id']), value);
            }
        }

        return map.get(productoId) ?? null;
    }

    private buildUbicacionInput(): FormField<any>['input'] {
        const usesParcelDropdown = this.env.appName === 'covidai' || this.env.appName === 'bravosol';

        if (usesParcelDropdown) {
            return {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.parcelas,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (it: FilteredValue<ParcelasModel>, context?: DropdownFilterContext<ParcelasModel>): boolean => {
                        if (it.value == null) {
                            return true;
                        }

                        const fincaId = this.resolveSelectedDropdownId('id_finca', this.lists.fincas, context);
                        const sectorId = this.resolveSelectedDropdownId('id_sector', this.lists.sectores, context);

                        if (this.env.appName === 'covidai' && sectorId !== '') {
                            return this.sameValue(it.value.id_sector, sectorId);
                        }

                        return this.sameValue(it.value.id_finca, fincaId);
                    }
                }
            };
        }

        if (this.env.appName === 'viverosmuzale') {
            return {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.parcelas,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (it: FilteredValue<ParcelasModel>, context?: DropdownFilterContext<ParcelasModel>): boolean => {
                        if (it.value == null) {
                            return true;
                        }

                        const sectorId = this.resolveSelectedDropdownId('id_sector', this.lists.sectores, context);

                        if (sectorId !== '') {
                            return this.sameValue(it.value.id_sector, sectorId);
                        }

                        const fincaId = this.resolveSelectedDropdownId('id_finca', this.lists.fincas, context);

                        return this.sameValue(it.value.id_finca, fincaId);
                    }
                }
            };
        }

        return {
            type: InputType.DROPDOWN_SEARCH,
            isEditable: true,
            data: this.env.appName === 'sendra' ? this.lists.parcelas :
                this.env.appName === 'viverosblanco'
                    ? this.lists.ubicacionesViveros
                    : this.lists.ubicaciones,
            dropdownConfiguration: {
                valuePrimaryKey: 'id'
            }
        };
    }

    private buildUbicacionDestinoInput(): FormField<any>['input'] {
        const usesParcelDropdown = this.env.appName === 'covidai' || this.env.appName === 'bravosol';

        if (usesParcelDropdown) {
            return {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.parcelasDestino,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (it: FilteredValue<ParcelasModel>, context?: DropdownFilterContext<ParcelasModel>): boolean => {
                        if (it.value == null) {
                            return true;
                        }

                        const fincaId = this.resolveSelectedDropdownId('id_finca', this.lists.fincas, context);

                        return this.sameValue(it.value.id_finca, fincaId);
                    }
                }
            };
        }

        return {
            type: InputType.DROPDOWN_SEARCH,
            isEditable: true,
            data: this.env.appName === 'sendra' ? this.lists.parcelasDestino : this.lists.ubicacionesViveros,
            dropdownConfiguration: {
                valuePrimaryKey: 'id'
            }
        };
    }


    private createDropdownDataSourceField(
        endpointKey: EntradasDropdownEndpointKey,
        labelKey: string,
        distinctBy: string | readonly string[],
        valueKey: string = 'id',
        mapOption?: ReactiveDropdownDataSource<StockageModel>['mapOption']
    ): Partial<Pick<FormField<any>, 'dataSource'>> {
        const dataSource = this.createDropdownDataSource(endpointKey, labelKey, distinctBy, valueKey, mapOption);

        return dataSource === undefined ? {} : { dataSource };
    }

    private createUbicacionDataSourceField(): Partial<Pick<FormField<any>, 'dataSource'>> {
        if (!this.usesRemoteParcelDropdown('ubicacion')) {
            return {};
        }

        return this.createDropdownDataSourceField(
            'parcelas',
            'nombre',
            'id',
            'id',
            (item): Value<ParcelasModel> => this.mapParcelOption(item)
        );
    }

    private createUbicacionDestinoDataSourceField(): Partial<Pick<FormField<any>, 'dataSource'>> {
        if (!this.usesRemoteParcelDropdown('ubicacion_destino')) {
            return {};
        }

        return this.createDropdownDataSourceField(
            'parcelas',
            'nombre',
            'id',
            'id',
            (item): Value<ParcelasModel> => this.mapParcelOption(item)
        );
    }

    private usesRemoteParcelDropdown(fieldName: 'ubicacion' | 'ubicacion_destino'): boolean {
        if (fieldName === 'ubicacion') {
            return ['covidai', 'bravosol', 'sendra', 'viverosmuzale'].includes(this.env.appName);
        }

        return ['covidai', 'bravosol', 'sendra'].includes(this.env.appName);
    }

    private createDropdownDataSource(
        endpointKey: EntradasDropdownEndpointKey,
        labelKey: string,
        distinctBy: string | readonly string[],
        valueKey: string,
        mapOption?: ReactiveDropdownDataSource<StockageModel>['mapOption']
    ): ReactiveDropdownDataSource<StockageModel> | undefined {
        const endpoint = this.dropdownEndpoints?.[endpointKey];

        if (endpoint === null || endpoint === undefined) {
            return undefined;
        }

        const normalizedDistinctBy: ReactiveDropdownKeySelector<StockageModel> = typeof distinctBy === 'string'
            ? distinctBy
            : [...distinctBy];
        const dataSource: ReactiveDropdownDataSource<StockageModel> = {
            endpoint,
            cacheKey: `stock-entradas-${String(endpointKey)}`,
            labelKey,
            valueKey,
            distinctBy: normalizedDistinctBy,
            cacheEmptyResults: false
        };

        if (mapOption !== undefined) {
            dataSource.mapOption = mapOption;
        }

        return dataSource;
    }

    private mapParcelOption(item: unknown): Value<ParcelasModel> {
        const parcela = this.isRecord(item) ? item : {};
        const finca = String(parcela['finca'] ?? '').trim();
        const nombre = String(parcela['nombre'] ?? '').trim();
        const label = finca === '' ? nombre : `${finca} - ${nombre}`;

        return {
            label,
            value: item as ParcelasModel,
            raw: item
        };
    }

    private firstToken(value: string | undefined): string {
        return String(value ?? '').split(';')[0] ?? '';
    }

    private resolveSelectedDropdownId<T extends object>(
        fieldName: string,
        dropdown: DropdownData<T>,
        context?: ReactiveFormContextReader
    ): string {
        const entity = this.resolveSelectedDropdownEntity(fieldName, dropdown, context);
        const entityId = this.getRecordValue(entity, 'id');

        if (entityId !== '') {
            return entityId;
        }

        return this.getReactiveValueTokens(this.getCurrentFieldValue(fieldName, context))[0] ?? '';
    }

    private resolveSelectedDropdownEntity<T extends object>(
        fieldName: string,
        dropdown: DropdownData<T>,
        context?: ReactiveFormContextReader
    ): T | null {
        const currentValue = this.getCurrentFieldValue(fieldName, context);

        if (this.isRecord(currentValue)) {
            return currentValue as unknown as T;
        }

        const tokens = this.getReactiveValueTokens(currentValue);

        if (tokens.length === 0) {
            return null;
        }

        const options = [...(dropdown.filtered ?? []), ...(dropdown.values ?? [])];

        for (const option of options) {
            const entity = option.value;

            if (!this.isRecord(entity)) {
                continue;
            }

            const optionTokens = this.getReactiveValueTokens(option.value)
                .concat(this.getReactiveValueTokens(option.label));

            if (tokens.some((token) => optionTokens.includes(token))) {
                return entity as T;
            }
        }

        return null;
    }

    private getCurrentFieldValue(
        fieldName: string,
        context?: ReactiveFormContextReader
    ): unknown {
        if (context && 'getValue' in context && typeof context.getValue === 'function') {
            return context.getValue(fieldName) ?? (this.model as Record<string, unknown>)[fieldName];
        }

        return context?.model.get(fieldName)?.value ?? (this.model as Record<string, unknown>)[fieldName];
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (this.isRecord(value)) {
            return Object.values(value).flatMap((item) => this.getReactiveValueTokens(item));
        }

        return String(value ?? '')
            .split(';')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
    }

    private getRecordValue(value: unknown, key: string): string {
        if (!this.isRecord(value)) {
            return '';
        }

        return String(value[key] ?? '').trim();
    }

    private hasNonEmptyValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).trim() !== '';
    }

    private sameValue(left: unknown, right: unknown): boolean {
        return String(left ?? '').trim() === String(right ?? '').trim();
    }

    private hiddenField(
        name: string,
        dependencySources: string[] = [],
        updateValue?: (context?: ReactiveFormContextReader) => string
    ): FormField<any> {
        const field: FormField<any> = {
            name,

            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: true
            },
            width: 1,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true
            },
            order: 999
        };

        if (updateValue) {
            field.computed = {
                dependsOn: dependencySources,
                resolve: (context) => updateValue(context),
            };
            field.updateValue = (_field, context) => updateValue(context);
        }

        return field;
    }


    public refreshLineFieldLabels(): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => {
                if (section.name !== 'Líneas') {
                    return section;
                }

                return {
                    ...section,
                    fields: section.fields.map((field) => {
                        if (field.name !== 'lineas_group' || !field.input.repeatableGroupConfiguration) {
                            return field;
                        }

                        return {
                            ...field,
                            input: {
                                ...field.input,
                                repeatableGroupConfiguration: {
                                    ...field.input.repeatableGroupConfiguration,
                                    subFields: field.input.repeatableGroupConfiguration.subFields.map((sf) => {
                                        if (sf.key === 'producto') {
                                            return {
                                                ...sf,
                                                label: this.getProductFieldLabel()
                                            };
                                        }

                                        if (sf.key === 'cantidad') {
                                            return {
                                                ...sf,
                                                label: this.env.applicationType === 'viverogest' &&
                                                    String(this.model.tipo_dato ?? '') === 'Planta'
                                                    ? 'Uds de bandejas'
                                                    : 'Cantidad'
                                            };
                                        }

                                        return sf;
                                    })
                                }
                            }
                        };
                    })
                };
            })
        };
    }

    public updateFincaLabel(isMov: boolean): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => ({
                ...section,
                fields: section.fields.map((field) => {
                    if (field.name !== 'id_finca') {
                        return field;
                    }
                    return {
                        ...field,
                        label: isMov ? 'Invernadero Destino' : this.env.fincaName
                    };
                })
            }))
        };
    }

    public updateFincaRequired(isRequired: boolean): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => ({
                ...section,
                fields: section.fields.map((field) => {
                    if (field.name !== 'id_finca') {
                        return field;
                    }
                    return {
                        ...field,
                        conditions: {
                            ...field.conditions,
                            isRequired
                        }
                    };
                })
            }))
        };
    }

    public get defaultDataType(): string | undefined {
        return this.env.forcesSeedType ? 'Semilla' : this.env.forcesSeedType ? 'Planta' : undefined;
    }
}
